import { useCallback, useRef, useState } from 'react'
import { EditorCaret, EditorLine } from './descriptionEditorCommands'
import { runLength } from './mobileDescription'

const HISTORY_LIMIT = 100

export interface HistoryCaret {
  index: number
  offset: number
}

export interface HistorySnapshot {
  value: string
  caret: HistoryCaret | null
}

export interface HistoryStacks {
  undo: HistorySnapshot[]
  redo: HistorySnapshot[]
}

interface HistoryStep {
  stacks: HistoryStacks
  restore: HistorySnapshot
}

export const emptyHistory = (): HistoryStacks => ({ undo: [], redo: [] })

export const pushHistory = (stacks: HistoryStacks, snapshot: HistorySnapshot): HistoryStacks => ({
  undo: [...stacks.undo, snapshot].slice(-HISTORY_LIMIT),
  redo: []
})

const take = (from: HistorySnapshot[], onto: HistorySnapshot[], current: HistorySnapshot): HistoryStep | null => {
  if (from.length === 0) {
    return null
  }
  return {
    stacks: { undo: from.slice(0, -1), redo: [...onto, current] },
    restore: from[from.length - 1]
  }
}

export const takeUndo = (stacks: HistoryStacks, current: HistorySnapshot): HistoryStep | null =>
  take(stacks.undo, stacks.redo, current)

export const takeRedo = (stacks: HistoryStacks, current: HistorySnapshot): HistoryStep | null => {
  const step = take(stacks.redo, stacks.undo, current)
  if (step === null) {
    return null
  }
  return { stacks: { undo: step.stacks.redo, redo: step.stacks.undo }, restore: step.restore }
}

interface HistoryKey {
  key: string
  ctrlKey: boolean
  metaKey: boolean
  shiftKey: boolean
  altKey: boolean
}

const withModifier = (event: HistoryKey): boolean => (event.metaKey || event.ctrlKey) && !event.altKey

export const isUndoKey = (event: HistoryKey): boolean =>
  withModifier(event) && !event.shiftKey && event.key.toLowerCase() === 'z'

export const isRedoKey = (event: HistoryKey): boolean => {
  if (!withModifier(event)) {
    return false
  }
  if (event.key.toLowerCase() === 'y' && !event.shiftKey) {
    return true
  }
  return event.shiftKey && event.key.toLowerCase() === 'z'
}

export const caretIndex = (lines: EditorLine[], caret: EditorCaret | null): HistoryCaret | null => {
  if (caret === null) {
    return null
  }
  const index = lines.findIndex((line) => line.id === caret.id)
  if (index < 0) {
    return null
  }
  return { index, offset: caret.offset }
}

export const caretFromHistory = (lines: EditorLine[], caret: HistoryCaret | null): EditorCaret | null => {
  if (lines.length === 0) {
    return null
  }
  const index = caret === null ? lines.length - 1 : Math.min(Math.max(caret.index, 0), lines.length - 1)
  const line = lines[index]
  const maxOffset = line.kind === 'rule' ? 0 : runLength(line.runs)
  const offset = caret === null ? maxOffset : Math.min(Math.max(caret.offset, 0), maxOffset)
  return { id: line.id, offset }
}

interface HistoryAvailability {
  undo: boolean
  redo: boolean
}

interface DescriptionHistory {
  remember: () => void
  commit: (previous: string, next: string) => void
  undo: () => HistorySnapshot | null
  redo: () => HistorySnapshot | null
  forget: () => void
  clear: () => void
  canUndo: boolean
  canRedo: boolean
}

const countsOf = (stacks: HistoryStacks): HistoryAvailability => ({
  undo: stacks.undo.length > 0,
  redo: stacks.redo.length > 0
})

export const useDescriptionHistory = (readSnapshot: () => HistorySnapshot): DescriptionHistory => {
  const stacks = useRef(emptyHistory())
  const pending = useRef<HistorySnapshot | null>(null)
  const read = useRef(readSnapshot)
  const [availability, setAvailability] = useState({ undo: false, redo: false })
  read.current = readSnapshot

  const refresh = (next: HistoryStacks): void => {
    stacks.current = next
    setAvailability(countsOf(next))
  }

  const remember = useCallback(() => {
    if (pending.current === null) {
      pending.current = read.current()
    }
  }, [])

  const commit = useCallback((previous: string, next: string) => {
    const snapshot = pending.current ?? { value: previous, caret: null }
    pending.current = null
    if (snapshot.value === next) {
      return
    }
    refresh(pushHistory(stacks.current, snapshot))
  }, [])

  const undo = useCallback((): HistorySnapshot | null => {
    pending.current = null
    const step = takeUndo(stacks.current, read.current())
    if (step === null) {
      return null
    }
    refresh(step.stacks)
    return step.restore
  }, [])

  const redo = useCallback((): HistorySnapshot | null => {
    pending.current = null
    const step = takeRedo(stacks.current, read.current())
    if (step === null) {
      return null
    }
    refresh(step.stacks)
    return step.restore
  }, [])

  const forget = useCallback(() => {
    pending.current = null
  }, [])

  const clear = useCallback(() => {
    pending.current = null
    refresh(emptyHistory())
  }, [])

  return {
    remember,
    commit,
    undo,
    redo,
    forget,
    clear,
    canUndo: availability.undo,
    canRedo: availability.redo
  }
}
